// PatientActionsBar.jsx
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Typography,
} from "@mui/material";

import BoltIcon from "@mui/icons-material/Bolt";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MoreVertIcon from "@mui/icons-material/MoreVert";

export default function PatientActionsBar({
  displayPatient,
  advancedMdPatientId,

  // ✅ IMPORTANT: esta debe ser la ruta que renderiza CreateClinicalNotePage
  addClinicalNotePath = "/CreateClinicalNotePage",
  addCarePlanPath = "/care-plan/new",

  quickActions = [
    { label: "Generate RAF", onClick: null, disabled: false },
    { label: "Generate HEDIS", onClick: null, disabled: false },
  ],

  taskOptions = [
    { value: "ASSIGN_TASK", label: "Assign a task to Care Coordinator", path: "/tasks/new" },
    { value: "CREATE_CAREPLAN", label: "Create a Care Plan", path: "/care-plan/new" },
    { value: "ASSIGN_PROFILE", label: "Assign Profile (PCP / Coder / Coordinator)", path: "/patient-profile/assign" },
    { value: "REVIEW_CAREGAPS", label: "Review Care Gaps", path: "/care-gaps" },
    { value: "REVIEW_CODING_OPPS", label: "Review Coding Opportunities", path: "/coding-opportunities" },
  ],

  onTaskConfirm,
}) {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);

  const [taskDialogOpen, setTaskDialogOpen] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState("");

  const cipId = displayPatient?.cipId || "";

  // ✅ CreateClinicalNotePage necesita patientId (AdvancedMD)
  const disabledNoPatientForClinicalNote = !advancedMdPatientId;
  const disabledNoPatientForInternal = !cipId;

  const ENABLE_ONLY_CLINICAL_NOTE = true;

  const ensureAbsolutePath = React.useCallback((p) => {
    if (!p) return "/";
    return p.startsWith("/") ? p : `/${p}`; // ✅ evita /clinical-information/...
  }, []);

  const goToClinicalNotes = React.useCallback(() => {
    if (!advancedMdPatientId) return;

    navigate(ensureAbsolutePath(addClinicalNotePath), {
      state: {
        patientId: String(advancedMdPatientId),
        patient: displayPatient || null,
        cipId: cipId || "",
      },
    });
  }, [advancedMdPatientId, navigate, addClinicalNotePath, displayPatient, cipId, ensureAbsolutePath]);

  const buildQueryString = React.useCallback(() => {
    const qs = new URLSearchParams();
    if (cipId) qs.set("cipId", cipId);
    if (advancedMdPatientId) qs.set("amdId", String(advancedMdPatientId));
    return qs.toString();
  }, [cipId, advancedMdPatientId]);

  const goTo = React.useCallback(
    (basePath) => {
      const path = ensureAbsolutePath(basePath);
      const qs = buildQueryString();
      navigate(qs ? `${path}?${qs}` : path);
    },
    [buildQueryString, navigate, ensureAbsolutePath]
  );

  const handleOpenTaskDialog = () => {
    if (ENABLE_ONLY_CLINICAL_NOTE) return;
    setSelectedTask("");
    setTaskDialogOpen(true);
  };

  const handleConfirmTask = async () => {
    if (ENABLE_ONLY_CLINICAL_NOTE) return;
    if (!selectedTask) return;

    const option = taskOptions.find((x) => x.value === selectedTask);

    const context = { cipId, advancedMdPatientId, displayPatient, task: option || null };

    try {
      await onTaskConfirm?.(selectedTask, context);
      if (option?.path) goTo(option.path);
      setTaskDialogOpen(false);
    } catch (err) {
      console.error("[PatientActionsBar] Task confirm error:", err);
    }
  };

  const disabledAddTask = ENABLE_ONLY_CLINICAL_NOTE || disabledNoPatientForInternal;
  const disabledQuickAction = ENABLE_ONLY_CLINICAL_NOTE || disabledNoPatientForInternal;
  const disabledCarePlan = ENABLE_ONLY_CLINICAL_NOTE || disabledNoPatientForInternal;
  const disabledMore = ENABLE_ONLY_CLINICAL_NOTE;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          px: 1,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Tooltip title={disabledAddTask ? "Coming soon" : "Create a task for this patient"}>
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<AssignmentTurnedInIcon />}
                onClick={handleOpenTaskDialog}
                disabled={disabledAddTask}
              >
                Add Task
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={disabledQuickAction ? "Coming soon" : "Quick actions for this patient"}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BoltIcon />}
                onClick={(e) => {
                  if (disabledQuickAction) return;
                  setAnchorEl(e.currentTarget);
                }}
                disabled={disabledQuickAction}
              >
                Quick Action
              </Button>
            </span>
          </Tooltip>

          {/* ✅ Add Clinical Note */}
          <Tooltip
            title={
              disabledNoPatientForClinicalNote
                ? "Missing AdvancedMD patientId"
                : "Go to create a clinical note"
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NoteAddIcon />}
                onClick={goToClinicalNotes}
                disabled={disabledNoPatientForClinicalNote}
              >
                Add Clinical Note
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={disabledCarePlan ? "Coming soon" : "Go to create a Care Plan"}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlaylistAddIcon />}
                onClick={() => {
                  if (disabledCarePlan) return;
                  goTo(addCarePlanPath);
                }}
                disabled={disabledCarePlan}
              >
                Add Care Plan
              </Button>
            </span>
          </Tooltip>

          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

          <Tooltip title={disabledMore ? "Coming soon" : "More"}>
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  if (disabledMore) return;
                  setAnchorEl(e.currentTarget);
                }}
                disabled={disabledMore}
              >
                <MoreVertIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
            {quickActions.map((a) => (
              <MenuItem
                key={a.label}
                disabled={true}
                onClick={() => {
                  setAnchorEl(null);
                  a.onClick?.();
                }}
              >
                {a.label}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Paper>

      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Task</DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a task to create for this patient.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="task-select-label">Task type</InputLabel>
            <Select
              labelId="task-select-label"
              value={selectedTask}
              onChange={(e) => setSelectedTask(e.target.value)}
              input={<OutlinedInput label="Task type" />}
            >
              {taskOptions.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTaskDialogOpen(false)} variant="text">
            Cancel
          </Button>
          <Button onClick={handleConfirmTask} variant="contained" disabled={!selectedTask || ENABLE_ONLY_CLINICAL_NOTE}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
